import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Input } from "./ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AddressAutocomplete = ({
  value,
  onChange,
  onSelect,
  className,
  placeholder,
  testid,
}) => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const pickedValue = useRef(null);
  const abortRef = useRef(null);
  const boxRef = useRef(null);

  useEffect(() => {
    const q = (value || "").trim();
    if (pickedValue.current !== null && value === pickedValue.current) {
      return;
    }
    pickedValue.current = null;
    if (q.length < 3) {
      setItems([]);
      setOpen(false);
      return;
    }
    const timer = setTimeout(async () => {
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      try {
        const { data } = await axios.get(`${API}/address/autocomplete`, {
          params: { q },
          signal: ctrl.signal,
        });
        const list = data?.suggestions || [];
        setItems(list);
        setOpen(list.length > 0);
        setActive(-1);
      } catch (err) {
        if (!axios.isCancel(err)) {
          setItems([]);
          setOpen(false);
        }
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [value]);

  useEffect(() => {
    const onDocDown = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocDown);
    return () => document.removeEventListener("mousedown", onDocDown);
  }, []);

  const pick = (item) => {
    pickedValue.current = item.formatted || "";
    onChange(item.formatted || "");
    onSelect?.(item);
    setItems([]);
    setOpen(false);
    setActive(-1);
  };

  const onKeyDown = (e) => {
    if (!open || items.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, items.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter" && active >= 0) {
      e.preventDefault();
      pick(items[active]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={boxRef} className="relative">
      <Input
        className={className}
        value={value}
        placeholder={placeholder}
        autoComplete="off"
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        onFocus={() => items.length > 0 && setOpen(true)}
        data-testid={testid}
      />
      {open && items.length > 0 && (
        <ul
          className="absolute z-50 mt-1 max-h-72 w-full overflow-auto border border-border bg-white shadow-lg"
          style={{ textTransform: "none", fontVariant: "normal", fontFamily: "ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif" }}
          data-testid={testid ? `${testid}-suggestions` : "address-suggestions"}
        >
          {items.map((it, i) => (
            <li key={`${it.formatted}-${i}`}>
              <button
                type="button"
                className={`block w-full px-3 py-2 text-left text-sm hover:bg-[#F7F2E7] ${
                  i === active ? "bg-[#F7F2E7]" : ""
                }`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(it)}
                data-testid={testid ? `${testid}-option-${i}` : `address-option-${i}`}
              >
                {it.formatted}
              </button>
            </li>
          ))}
          <li className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
            Addresses via Geoapify
          </li>
        </ul>
      )}
    </div>
  );
};
